import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail } from "lucide-react";

/**
 * /forgot-password
 *
 * Public. Sends a set-password link to the entered email if an account
 * exists. Backend always returns 200 to avoid leaking account existence.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!email.trim()) return toast.error("Enter your email");
    setSubmitting(true);
    try {
      await api.post("/auth/request-password-reset", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Layout>
      <SEO title="Reset your password" description="Reset your South on Leadership password." noindex />
      <section className="container-narrow py-16 md:py-24 max-w-md">
        <div className="bg-white border border-brand-line p-8 md:p-10">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <Mail className="w-8 h-8 text-brand-rust mx-auto" />
              <h1 className="font-display text-3xl mt-5 text-brand-ink leading-tight">Check your inbox</h1>
              <p className="mt-3 text-brand-inkMuted leading-relaxed">
                If <strong className="text-brand-ink">{email}</strong> matches an account, we&apos;ve just sent a one-click link to set a new password. The link works for 7 days.
              </p>
              <p className="mt-6 text-xs text-brand-inkMuted">
                Didn&apos;t arrive? Check your spam folder, or{" "}
                <button onClick={() => setSent(false)} className="text-brand-rust hover:text-brand-ink underline underline-offset-4">try a different email</button>.
              </p>
            </div>
          ) : (
            <>
              <Mail className="w-8 h-8 text-brand-rust" />
              <h1 className="font-display text-3xl md:text-4xl mt-5 text-brand-ink leading-tight">
                Forgot your password?
              </h1>
              <p className="mt-3 text-brand-inkMuted text-sm leading-relaxed">
                Enter the email on your account and we&apos;ll send you a one-click link to set a new password.
              </p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-password-form">
                <input
                  required
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent border-b border-brand-line py-3 text-brand-ink focus:outline-none focus:border-brand-rust"
                  data-testid="forgot-password-email"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="forgot-password-submit"
                  className="w-full bg-brand-ink text-white hover:bg-black px-7 py-4 text-sm font-medium tracking-wide transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send me the link"}
                </button>
              </form>
              <p className="mt-6 text-xs text-brand-inkMuted text-center">
                Remembered it?{" "}
                <Link to="/login" className="text-brand-rust hover:text-brand-ink underline underline-offset-4">Back to sign in</Link>
              </p>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
